import { useState, useEffect, useCallback } from 'react';
import API from '@/utils/api';
import { toast } from 'sonner';
import { useDemo } from '@/contexts/DemoContext';

/**
 * useSharedMedicalData - Single source of truth for medical data
 * 
 * This hook provides unified access to all shared medical data:
 * - Profile (name, DOB, blood type)
 * - Allergies
 * - Medications
 * - Conditions
 * - Surgeries
 * - Side Effects
 * - Emergency Contacts
 * - Doctors
 * - Insurance
 * 
 * In DEMO MODE, data is read from and written to local storage
 * via the DemoContext instead of the backend API.
 * 
 * Both Medical section and Emergency Binder use this same hook,
 * ensuring data consistency across all views.
 */
const useSharedMedicalData = () => {
  const { isDemoMode, demoData, 
    getAllergies, addAllergy: demoAddAllergy, updateAllergy: demoUpdateAllergy, deleteAllergy: demoDeleteAllergy,
    getMedications, addMedication: demoAddMedication, updateMedication: demoUpdateMedication, deleteMedication: demoDeleteMedication,
    getConditions, addCondition: demoAddCondition, updateCondition: demoUpdateCondition, deleteCondition: demoDeleteCondition,
    getSurgeries, addSurgery: demoAddSurgery, updateSurgery: demoUpdateSurgery, deleteSurgery: demoDeleteSurgery,
    getEmergencyContacts, addEmergencyContact: demoAddEmergencyContact, updateEmergencyContact: demoUpdateEmergencyContact, deleteEmergencyContact: demoDeleteEmergencyContact,
    getDoctors, addDoctor: demoAddDoctor, updateDoctor: demoUpdateDoctor, deleteDoctor: demoDeleteDoctor,
    getInsurance, addInsurance: demoAddInsurance, updateInsurance: demoUpdateInsurance, deleteInsurance: demoDeleteInsurance,
    getMedicalProfile, updateMedicalProfile: demoUpdateMedicalProfile,
    getSharedMedicalSummary
  } = useDemo();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // All data states
  const [profile, setProfile] = useState(null);
  const [allergies, setAllergies] = useState([]);
  const [medications, setMedications] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [surgeries, setSurgeries] = useState([]);
  const [sideEffects, setSideEffects] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [insurance, setInsurance] = useState([]);
  
  // Summary data
  const [summary, setSummary] = useState(null);

  // ============= FETCH ALL DATA =============
  
  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // DEMO MODE: Use local data from context
      if (isDemoMode) {
        const demoSummary = getSharedMedicalSummary();
        if (demoSummary) {
          setProfile(demoSummary.profile || {});
          setAllergies(demoSummary.allergies || []);
          setMedications(demoSummary.medications || []);
          setConditions(demoSummary.conditions || []);
          setSurgeries(demoSummary.surgeries || []);
          setSideEffects(demoSummary.side_effects || []);
          setEmergencyContacts(demoSummary.emergency_contacts || []);
          setDoctors(demoSummary.doctors || []);
          setInsurance(demoSummary.insurance || []);
          setSummary(demoSummary);
        }
        setLoading(false);
        return;
      }
      
      // REAL MODE: Fetch from API
      const response = await API.get('/shared-medical/summary');
      const data = response.data;
      
      setProfile(data.profile || {});
      setAllergies(data.allergies || []);
      setMedications(data.medications || []);
      setConditions(data.conditions || []);
      setSurgeries(data.surgeries || []);
      setSideEffects(data.side_effects || []);
      setEmergencyContacts(data.emergency_contacts || []);
      setDoctors(data.doctors || []);
      setInsurance(data.insurance || []);
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch shared medical data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isDemoMode, getSharedMedicalSummary]);

  // Initial fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Refetch when demo data changes
  useEffect(() => {
    if (isDemoMode && demoData) {
      const demoSummary = getSharedMedicalSummary();
      if (demoSummary) {
        setProfile(demoSummary.profile || {});
        setAllergies(demoSummary.allergies || []);
        setMedications(demoSummary.medications || []);
        setConditions(demoSummary.conditions || []);
        setSurgeries(demoSummary.surgeries || []);
        setSideEffects(demoSummary.side_effects || []);
        setEmergencyContacts(demoSummary.emergency_contacts || []);
        setDoctors(demoSummary.doctors || []);
        setInsurance(demoSummary.insurance || []);
        setSummary(demoSummary);
      }
    }
  }, [isDemoMode, demoData, getSharedMedicalSummary]);

  // ============= PROFILE =============
  
  const updateProfile = useCallback(async (updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = demoUpdateMedicalProfile(updates);
        setProfile(updated);
        return updated;
      }
      
      // REAL MODE
      const response = await API.put('/shared-medical/profile', updates);
      setProfile(response.data);
      return response.data;
    } catch (err) {
      toast.error('Failed to update profile');
      throw err;
    }
  }, [isDemoMode, demoUpdateMedicalProfile]);

  // ============= ALLERGIES =============
  
  const addAllergy = useCallback(async (allergy) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const newAllergy = demoAddAllergy(allergy);
        setAllergies(prev => [...prev, newAllergy]);
        return newAllergy;
      }
      
      // REAL MODE
      const response = await API.post('/shared-medical/allergies', allergy);
      setAllergies(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      toast.error('Failed to add allergy');
      throw err;
    }
  }, [isDemoMode, demoAddAllergy]);

  const updateAllergy = useCallback(async (id, updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = demoUpdateAllergy(id, updates);
        setAllergies(prev => prev.map(a => a.id === id ? updated : a));
        return updated;
      }
      
      // REAL MODE
      const response = await API.put(`/shared-medical/allergies/${id}`, updates);
      setAllergies(prev => prev.map(a => a.id === id ? response.data : a));
      return response.data;
    } catch (err) {
      toast.error('Failed to update allergy');
      throw err;
    }
  }, [isDemoMode, demoUpdateAllergy]);

  const deleteAllergy = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        demoDeleteAllergy(id);
        setAllergies(prev => prev.filter(a => a.id !== id));
        return;
      }
      
      // REAL MODE
      await API.delete(`/shared-medical/allergies/${id}`);
      setAllergies(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      toast.error('Failed to delete allergy');
      throw err;
    }
  }, [isDemoMode, demoDeleteAllergy]);

  // ============= MEDICATIONS =============
  
  const addMedication = useCallback(async (medication) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const newMedication = demoAddMedication(medication);
        setMedications(prev => [...prev, newMedication]);
        return newMedication;
      }
      
      // REAL MODE
      const response = await API.post('/shared-medical/medications', medication);
      setMedications(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      toast.error('Failed to add medication');
      throw err;
    }
  }, [isDemoMode, demoAddMedication]);

  const updateMedication = useCallback(async (id, updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = demoUpdateMedication(id, updates);
        setMedications(prev => prev.map(m => m.id === id ? updated : m));
        return updated;
      }
      
      // REAL MODE
      const response = await API.put(`/shared-medical/medications/${id}`, updates);
      setMedications(prev => prev.map(m => m.id === id ? response.data : m));
      return response.data;
    } catch (err) {
      toast.error('Failed to update medication');
      throw err;
    }
  }, [isDemoMode, demoUpdateMedication]);

  const deleteMedication = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        demoDeleteMedication(id);
        setMedications(prev => prev.filter(m => m.id !== id));
        return;
      }
      
      // REAL MODE
      await API.delete(`/shared-medical/medications/${id}`);
      setMedications(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      toast.error('Failed to delete medication');
      throw err;
    }
  }, [isDemoMode, demoDeleteMedication]);

  // ============= CONDITIONS =============
  
  const addCondition = useCallback(async (condition) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const newCondition = demoAddCondition(condition);
        setConditions(prev => [...prev, newCondition]);
        return newCondition;
      }
      
      // REAL MODE
      const response = await API.post('/shared-medical/conditions', condition);
      setConditions(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      toast.error('Failed to add condition');
      throw err;
    }
  }, [isDemoMode, demoAddCondition]);

  const updateCondition = useCallback(async (id, updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = demoUpdateCondition(id, updates);
        setConditions(prev => prev.map(c => c.id === id ? updated : c));
        return updated;
      }
      
      // REAL MODE
      const response = await API.put(`/shared-medical/conditions/${id}`, updates);
      setConditions(prev => prev.map(c => c.id === id ? response.data : c));
      return response.data;
    } catch (err) {
      toast.error('Failed to update condition');
      throw err;
    }
  }, [isDemoMode, demoUpdateCondition]);

  const deleteCondition = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        demoDeleteCondition(id);
        setConditions(prev => prev.filter(c => c.id !== id));
        return;
      }
      
      // REAL MODE
      await API.delete(`/shared-medical/conditions/${id}`);
      setConditions(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      toast.error('Failed to delete condition');
      throw err;
    }
  }, [isDemoMode, demoDeleteCondition]);

  // ============= SURGERIES =============
  
  const addSurgery = useCallback(async (surgery) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const newSurgery = demoAddSurgery(surgery);
        setSurgeries(prev => [...prev, newSurgery]);
        return newSurgery;
      }
      
      // REAL MODE
      const response = await API.post('/shared-medical/surgeries', surgery);
      setSurgeries(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      toast.error('Failed to add surgery');
      throw err;
    }
  }, [isDemoMode, demoAddSurgery]);

  const updateSurgery = useCallback(async (id, updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = demoUpdateSurgery(id, updates);
        setSurgeries(prev => prev.map(s => s.id === id ? updated : s));
        return updated;
      }
      
      // REAL MODE
      const response = await API.put(`/shared-medical/surgeries/${id}`, updates);
      setSurgeries(prev => prev.map(s => s.id === id ? response.data : s));
      return response.data;
    } catch (err) {
      toast.error('Failed to update surgery');
      throw err;
    }
  }, [isDemoMode, demoUpdateSurgery]);

  const deleteSurgery = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        demoDeleteSurgery(id);
        setSurgeries(prev => prev.filter(s => s.id !== id));
        return;
      }
      
      // REAL MODE
      await API.delete(`/shared-medical/surgeries/${id}`);
      setSurgeries(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      toast.error('Failed to delete surgery');
      throw err;
    }
  }, [isDemoMode, demoDeleteSurgery]);

  // ============= SIDE EFFECTS =============
  
  const addSideEffect = useCallback(async (effect) => {
    try {
      // DEMO MODE - Side effects not heavily used in demo, but support it
      if (isDemoMode) {
        const newEffect = { ...effect, id: `demo_se_${Date.now()}` };
        setSideEffects(prev => [...prev, newEffect]);
        return newEffect;
      }
      
      // REAL MODE
      const response = await API.post('/shared-medical/side-effects', effect);
      setSideEffects(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      toast.error('Failed to add side effect');
      throw err;
    }
  }, [isDemoMode]);

  const deleteSideEffect = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        setSideEffects(prev => prev.filter(e => e.id !== id));
        return;
      }
      
      // REAL MODE
      await API.delete(`/shared-medical/side-effects/${id}`);
      setSideEffects(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      toast.error('Failed to delete side effect');
      throw err;
    }
  }, [isDemoMode]);

  // ============= EMERGENCY CONTACTS =============
  
  const addEmergencyContact = useCallback(async (contact) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const newContact = demoAddEmergencyContact(contact);
        if (newContact.is_primary) {
          setEmergencyContacts(prev => prev.map(c => ({ ...c, is_primary: false })).concat(newContact));
        } else {
          setEmergencyContacts(prev => [...prev, newContact]);
        }
        return newContact;
      }
      
      // REAL MODE
      const response = await API.post('/shared-medical/emergency-contacts', contact);
      // If new contact is primary, update others in state
      if (response.data.is_primary) {
        setEmergencyContacts(prev => prev.map(c => ({ ...c, is_primary: false })).concat(response.data));
      } else {
        setEmergencyContacts(prev => [...prev, response.data]);
      }
      return response.data;
    } catch (err) {
      toast.error('Failed to add contact');
      throw err;
    }
  }, [isDemoMode, demoAddEmergencyContact]);

  const updateEmergencyContact = useCallback(async (id, updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = demoUpdateEmergencyContact(id, updates);
        setEmergencyContacts(prev => {
          if (updated?.is_primary) {
            return prev.map(c => c.id === id ? updated : { ...c, is_primary: false });
          }
          return prev.map(c => c.id === id ? updated : c);
        });
        return updated;
      }
      
      // REAL MODE
      const response = await API.put(`/shared-medical/emergency-contacts/${id}`, updates);
      setEmergencyContacts(prev => {
        // If updated to primary, remove primary from others
        if (response.data.is_primary) {
          return prev.map(c => c.id === id ? response.data : { ...c, is_primary: false });
        }
        return prev.map(c => c.id === id ? response.data : c);
      });
      return response.data;
    } catch (err) {
      toast.error('Failed to update contact');
      throw err;
    }
  }, [isDemoMode, demoUpdateEmergencyContact]);

  const deleteEmergencyContact = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        demoDeleteEmergencyContact(id);
        setEmergencyContacts(prev => prev.filter(c => c.id !== id));
        return;
      }
      
      // REAL MODE
      await API.delete(`/shared-medical/emergency-contacts/${id}`);
      setEmergencyContacts(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      toast.error('Failed to delete contact');
      throw err;
    }
  }, [isDemoMode, demoDeleteEmergencyContact]);

  // ============= DOCTORS =============
  
  const addDoctor = useCallback(async (doctor) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const newDoctor = demoAddDoctor(doctor);
        if (newDoctor.is_primary) {
          setDoctors(prev => prev.map(d => ({ ...d, is_primary: false })).concat(newDoctor));
        } else {
          setDoctors(prev => [...prev, newDoctor]);
        }
        return newDoctor;
      }
      
      // REAL MODE
      const response = await API.post('/shared-medical/doctors', doctor);
      if (response.data.is_primary) {
        setDoctors(prev => prev.map(d => ({ ...d, is_primary: false })).concat(response.data));
      } else {
        setDoctors(prev => [...prev, response.data]);
      }
      return response.data;
    } catch (err) {
      toast.error('Failed to add doctor');
      throw err;
    }
  }, [isDemoMode, demoAddDoctor]);

  const updateDoctor = useCallback(async (id, updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = demoUpdateDoctor(id, updates);
        setDoctors(prev => {
          if (updated?.is_primary) {
            return prev.map(d => d.id === id ? updated : { ...d, is_primary: false });
          }
          return prev.map(d => d.id === id ? updated : d);
        });
        return updated;
      }
      
      // REAL MODE
      const response = await API.put(`/shared-medical/doctors/${id}`, updates);
      setDoctors(prev => {
        if (response.data.is_primary) {
          return prev.map(d => d.id === id ? response.data : { ...d, is_primary: false });
        }
        return prev.map(d => d.id === id ? response.data : d);
      });
      return response.data;
    } catch (err) {
      toast.error('Failed to update doctor');
      throw err;
    }
  }, [isDemoMode, demoUpdateDoctor]);

  const deleteDoctor = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        demoDeleteDoctor(id);
        setDoctors(prev => prev.filter(d => d.id !== id));
        return;
      }
      
      // REAL MODE
      await API.delete(`/shared-medical/doctors/${id}`);
      setDoctors(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      toast.error('Failed to delete doctor');
      throw err;
    }
  }, [isDemoMode, demoDeleteDoctor]);

  // ============= INSURANCE =============
  
  const addInsurance = useCallback(async (insuranceData) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const newInsurance = demoAddInsurance(insuranceData);
        if (newInsurance.is_primary) {
          setInsurance(prev => prev.map(i => 
            i.coverage_type === newInsurance.coverage_type ? { ...i, is_primary: false } : i
          ).concat(newInsurance));
        } else {
          setInsurance(prev => [...prev, newInsurance]);
        }
        return newInsurance;
      }
      
      // REAL MODE
      const response = await API.post('/shared-medical/insurance', insuranceData);
      if (response.data.is_primary) {
        setInsurance(prev => prev.map(i => 
          i.coverage_type === response.data.coverage_type ? { ...i, is_primary: false } : i
        ).concat(response.data));
      } else {
        setInsurance(prev => [...prev, response.data]);
      }
      return response.data;
    } catch (err) {
      toast.error('Failed to add insurance');
      throw err;
    }
  }, [isDemoMode, demoAddInsurance]);

  const updateInsurance = useCallback(async (id, updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = demoUpdateInsurance(id, updates);
        setInsurance(prev => {
          if (updated?.is_primary) {
            return prev.map(i => 
              i.id === id ? updated : 
              i.coverage_type === updated.coverage_type ? { ...i, is_primary: false } : i
            );
          }
          return prev.map(i => i.id === id ? updated : i);
        });
        return updated;
      }
      
      // REAL MODE
      const response = await API.put(`/shared-medical/insurance/${id}`, updates);
      setInsurance(prev => {
        if (response.data.is_primary) {
          return prev.map(i => 
            i.id === id ? response.data : 
            i.coverage_type === response.data.coverage_type ? { ...i, is_primary: false } : i
          );
        }
        return prev.map(i => i.id === id ? response.data : i);
      });
      return response.data;
    } catch (err) {
      toast.error('Failed to update insurance');
      throw err;
    }
  }, [isDemoMode, demoUpdateInsurance]);

  const deleteInsurance = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        demoDeleteInsurance(id);
        setInsurance(prev => prev.filter(i => i.id !== id));
        return;
      }
      
      // REAL MODE
      await API.delete(`/shared-medical/insurance/${id}`);
      setInsurance(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      toast.error('Failed to delete insurance');
      throw err;
    }
  }, [isDemoMode, demoDeleteInsurance]);

  // ============= COMPUTED VALUES =============
  
  const activeMedications = medications.filter(m => m.is_active);
  const primaryEmergencyContact = emergencyContacts.find(c => c.is_primary) || emergencyContacts[0];
  const primaryDoctor = doctors.find(d => d.is_primary) || doctors[0];
  const primaryInsurance = insurance.find(i => i.is_primary) || insurance[0];

  // Completion percentage for Emergency Binder
  const completionPercentage = (() => {
    let filled = 0;
    let total = 7; // Main sections
    
    if (allergies.length > 0) filled++;
    if (medications.length > 0) filled++;
    if (conditions.length > 0) filled++;
    if (emergencyContacts.length > 0) filled++;
    if (doctors.length > 0) filled++;
    if (insurance.length > 0) filled++;
    if (profile?.blood_type) filled++;
    
    return Math.round((filled / total) * 100);
  })();

  return {
    // Loading state
    loading,
    error,
    refetch: fetchAllData,
    
    // Profile
    profile,
    updateProfile,
    
    // Allergies
    allergies,
    addAllergy,
    updateAllergy,
    deleteAllergy,
    
    // Medications
    medications,
    activeMedications,
    addMedication,
    updateMedication,
    deleteMedication,
    
    // Conditions
    conditions,
    addCondition,
    updateCondition,
    deleteCondition,
    
    // Surgeries
    surgeries,
    addSurgery,
    updateSurgery,
    deleteSurgery,
    
    // Side Effects
    sideEffects,
    addSideEffect,
    deleteSideEffect,
    
    // Emergency Contacts
    emergencyContacts,
    primaryEmergencyContact,
    addEmergencyContact,
    updateEmergencyContact,
    deleteEmergencyContact,
    
    // Doctors
    doctors,
    primaryDoctor,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    
    // Insurance
    insurance,
    primaryInsurance,
    addInsurance,
    updateInsurance,
    deleteInsurance,
    
    // Summary
    summary,
    completionPercentage,
  };
};

export default useSharedMedicalData;
